import React from 'react';
import { AlertTriangle, Flame, CalendarX } from 'lucide-react';

const STATUS_STYLES = {
  under: { bar: 'bg-[#5B8DEF]', chip: 'bg-[#12233F] text-[#7EA9F5]', label: 'Under-booked' },
  ok: { bar: 'bg-[#34C98E]', chip: 'bg-[#0E2B21] text-[#5FE0AF]', label: 'Healthy' },
  high: { bar: 'bg-[#FFD66B]', chip: 'bg-[#3A2F10] text-[#FFD66B]', label: 'Near max' },
  over: { bar: 'bg-[#FF5C7A]', chip: 'bg-[#3A1420] text-[#FF7BA9]', label: 'Over limit' },
};
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const UtilizationBoard = ({ data }) => {
  const { staff, conflicts, ot_risk } = data;
  return (
    <div className="grid lg:grid-cols-5 gap-4">
      {/* Utilization bars */}
      <div className="lg:col-span-3 bg-white/[0.03] border border-white/10 rounded-2xl p-4 space-y-3" data-testid="utilization-board">
        <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">Staff utilization — this week</h3>
        {staff.map(s => {
          const st = STATUS_STYLES[s.status];
          const pct = Math.min(s.utilization_pct, 120);
          return (
            <div key={s.id} className="space-y-1" data-testid={`util-row-${s.id}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">{s.name}</span>
                <span className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.chip}`}>{st.label}</span>
                  <span className="font-bold tabular-nums">{s.hours_total}h <span className="text-white/40 font-normal">/ {s.target}h</span></span>
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                <div className={`h-full ${st.bar} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <div className="flex items-center justify-between text-[10px] text-white/35">
                <span>{s.hours_draft > 0 ? `${s.hours_published}h live + ${s.hours_draft}h draft` : `${s.utilization_pct}% of target`}</span>
                {s.open_days.length > 0 && (
                  <span className="text-[#7EA9F5]">Open: {s.open_days.map(d => DAY_SHORT[new Date(d + 'T12:00:00').getDay()]).join(' · ')}</span>
                )}
              </div>
            </div>
          );
        })}
        {staff.length === 0 && <p className="text-sm text-white/40 py-6 text-center">No schedulable staff for this filter.</p>}
      </div>

      {/* Risks column */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4" data-testid="ot-risk-panel">
          <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider flex items-center gap-1.5 mb-2">
            <Flame className="h-4 w-4 text-[#FF7BA9]" /> Overtime risk
          </h3>
          {ot_risk.length === 0 ? <p className="text-xs text-white/35">No one is trending over their limit. 👌</p> : (
            <div className="space-y-1.5">
              {ot_risk.map(o => (
                <div key={o.employee_id} className="flex justify-between text-sm bg-[#3A1420]/60 rounded-lg px-3 py-2">
                  <span className="font-semibold">{o.name}</span>
                  <span className="text-[#FF7BA9] font-bold">{o.hours}h <span className="text-white/40 font-normal">(limit {Math.min(o.target, 40)}h)</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4" data-testid="conflict-radar">
          <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider flex items-center gap-1.5 mb-2">
            <AlertTriangle className="h-4 w-4 text-[#FFD66B]" /> Conflict radar ({conflicts.length})
          </h3>
          {conflicts.length === 0 ? <p className="text-xs text-white/35">No double-bookings, buffer, or availability violations.</p> : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {conflicts.map((c, i) => (
                <div key={i} className="text-xs bg-white/5 rounded-lg px-3 py-2 leading-relaxed" data-testid={`conflict-${i}`}>
                  <span className={`inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 rounded mr-1.5 ${c.kind === 'overlap' ? 'bg-[#3A1420] text-[#FF7BA9]' : 'bg-[#3A2F10] text-[#FFD66B]'}`}>
                    {c.kind === 'line_mismatch' ? 'wrong line' : c.kind}
                  </span>
                  {c.detail}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UtilizationBoard;
